import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';
import { Formik, Field, Form } from 'formik';
import { Button, Row, Col, message } from 'antd';
import {
  TextInput,
  TextArea,
  RadioGroup,
  MultiSelect,
  NumericInput,
  TagInput,
  DatePickerInput
} from '@components/form';
import { OfficeField, PropertyField } from '@containers/form';
import API from '@utils/api';
import CouponsActions, { CouponsSelectors } from '@redux/CouponsRedux';
import validationSchema from './schema';
import history from '@utils/history';
import { getErrors } from '@utils/error';
import { DAYS } from '@services/transform';

function transfromToFormValue(data, offices) {
  const {
    couponID,
    propertyID,
    officeID,
    percentage,
    userID,
    createdAt,
    isFixedAmount,
    amount,
    aliases,
    expireAt,
    usageTotal,
    minSpend,
    ...resetFields
  } = data;

  const newObj = {
    discountType: isFixedAmount ? '$' : '%',
    amount: isFixedAmount ? amount / 100 : amount
  };
  Object.keys(resetFields).forEach(objKey => {
    if (resetFields[objKey]) {
      newObj[objKey] = resetFields[objKey];
    }
  });

  if (!expireAt) {
    newObj.expireAt = null;
  } else {
    newObj.expireAt = moment(expireAt);
  }

  if (minSpend) {
    newObj.minSpend = minSpend / 100;
  }

  if (aliases && aliases.length > 0) {
    newObj.aliases = aliases
      .map(item => (item.status === 'active' ? item.name : null))
      .filter(item => !!item);
  }

  if (usageTotal) {
    newObj.usageTotal = usageTotal;
  } else {
    newObj.usageTotal = 0;
  }

  return newObj;
}

function transformRequest(formValues, initialData) {
  const {
    aliases,
    amount,
    discountType,
    serviceType,
    type,
    office,
    property,
    expireAt,
    minSpend,
    days,
    ...restValues
  } = formValues;

  let { aliases: oldAliases } = initialData || {};
  if (!oldAliases) {
    oldAliases = [];
  }

  const newObj = {
    isFixedAmount: discountType === '$',
    aliases: aliases
      .map(item => {
        const found = oldAliases.find(oldItem => oldItem.name === item);
        if (found) {
          if (found.status !== 'active') {
            return { ...found, name: item, status: 'active' };
          } else {
            return null;
          }
        } else {
          return { name: item, status: 'active' };
        }
      })
      .filter(item => !!item),
    ...restValues
  };

  if (expireAt) {
    newObj.expireAt = expireAt;
  }

  if (serviceType && serviceType.length > 0) {
    newObj.serviceType = serviceType;
  }

  if (type && type.length > 0) {
    newObj.type = type;
  }

  if (days && days.length > 0) {
    newObj.days = days;
  }

  if (office) {
    newObj.officeID = office.key;
  }

  if (property && property.length > 0) {
    newObj.propertyID = property[0].key;
  }

  if (discountType === '$') {
    newObj.amount = amount;
  } else {
    newObj.percentage = amount;
  }

  if (minSpend) {
    newObj.minSpend = minSpend * 100;
  }
  return newObj;
}

class CouponForm extends Component {
  handleSubmit = async (values, actions) => {
    const { addCoupon, updateCoupon, couponId, couponData } = this.props;
    const { setSubmitting } = actions;
    setSubmitting(true);
    try {
      const transformedValues = transformRequest(values, couponData);
      if (couponId) {
        let { result } = await API.updateCoupon(couponId, transformedValues);
        updateCoupon(result);
        history.goBack();
      } else {
        let { result } = await API.createCoupon(transformedValues);
        addCoupon(result);
        const { couponID } = result;
        history.replace(`/coupons/${couponID}`);
      }
      setSubmitting(false);
    } catch (err) {
      const error = getErrors(err);
      message.error(error.message);
    }
    setSubmitting(false);
  };

  renderForm = props => {
    const { isValid, isSubmitting } = props;
    const { couponId } = this.props;
    return (
      <Form className="create-form ant-form-vertical">
        <Row display="flex">
          <Row gutter={16}>
            <Col span={6}>
              <Field
                component={TextInput}
                mb={4}
                name="title"
                type="text"
                label="Title *"
              />
            </Col>
            <Col span={12}>
              <Field
                component={TagInput}
                mode="tags"
                name="aliases"
                label="Aliases *"
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Field
                component={RadioGroup}
                name="discountType"
                label="Discount Type *"
                options={[
                  { label: '%', value: '%' },
                  { label: '$', value: '$' }
                ]}
              />
            </Col>
            <Col span={6}>
              <Field
                component={NumericInput}
                mb={4}
                name="amount"
                type="number"
                label="Discount *"
              />
            </Col>
            <Col span={6}>
              <Field
                component={RadioGroup}
                name="status"
                label="Status"
                options={[
                  { label: 'Inactive', value: 'deactivated' },
                  { label: 'Active', value: 'active' }
                ]}
              />
            </Col>
          </Row>
          <Row justify="space-between" gutter={16}>
            <Col span={6}>
              <Field
                component={NumericInput}
                mb={4}
                name="minSpend"
                type="number"
                label="Minimum Spend"
              />
            </Col>
            <Col span={6}>
              <Field
                component={MultiSelect}
                mb={4}
                name="type"
                options={[
                  { label: 'Single', value: 'single' },
                  { label: 'Recurring', value: 'recurring' }
                ]}
                type="select"
                mode="multiple"
                label="Service Frequency"
              />
            </Col>
            <Col span={6}>
              <Field
                component={MultiSelect}
                mb={4}
                name="serviceType"
                options={[
                  { label: 'Hotel Style', value: 'hotel-style' },
                  { label: 'Deep Clean', value: 'deep-clean' },
                  { label: 'Move Out', value: 'move-out' }
                ]}
                mode="multiple"
                type="select"
                label="Service Type"
              />
            </Col>
          </Row>

          <Row justify="space-between" gutter={16}>
            <Col span={6}>
              <Field
                component={OfficeField}
                name="office"
                label="Office"
                mode="single"
                showSearch
              />
            </Col>
            <Col span={6}>
              <Field
                component={PropertyField}
                name="property"
                type="select"
                mode="single"
                label="Property"
              />
            </Col>
            <Col span={6}>
              <Field
                component={DatePickerInput}
                mb={4}
                name="expireAt"
                type="date"
                label="Expires At"
              />
            </Col>
          </Row>

          <Row justify="space-between" gutter={16}>
            <Col span={6}>
              <Field
                component={NumericInput}
                mb={4}
                name="usageTotal"
                type="number"
                label="Total Uses"
              />
            </Col>
            <Col span={6}>
              <Field
                component={NumericInput}
                mb={4}
                name="usagePerUser"
                type="number"
                label="Uses Per User *"
              />
            </Col>
            <Col span={6}>
              <Field
                component={MultiSelect}
                mb={4}
                name="days"
                options={[
                  { label: 'Monday', value: DAYS.monday },
                  { label: 'Tuesday', value: DAYS.tuesday },
                  { label: 'Wednesday', value: DAYS.wednesday },
                  { label: 'Thursday', value: DAYS.thursday },
                  { label: 'Friday', value: DAYS.friday },
                  { label: 'Saturday', value: DAYS.saturday }
                ]}
                mode="multiple"
                type="select"
                label="Days"
              />
            </Col>
          </Row>

          <Field component={TextArea} name="notes" type="text" label="Notes" />

          <Row>
            <Col offset={9} span={6}>
              <Button
                flex={1}
                mb={4}
                type="primary"
                htmlType="submit"
                className="btn-submit"
                disabled={!isValid || isSubmitting}
              >
                {couponId ? 'Update Coupon' : 'Create Coupon'}
              </Button>
            </Col>
          </Row>
        </Row>
      </Form>
    );
  };

  render() {
    const { couponData } = this.props;
    const initialValues = couponData
      ? transfromToFormValue(couponData)
      : {
          serviceType: ['hotel-style', 'deep-clean'],
          type: ['single', 'recurring'],
          amount: 0,
          usageTotal: 0,
          discountType: '$',
          status: 'active',
          aliases: [],
          title: '',
          days: []
        };

    return (
      <Formik
        initialValues={initialValues}
        onSubmit={this.handleSubmit}
        validationSchema={validationSchema}
        render={this.renderForm}
        isInitialValid={validationSchema.isValidSync(initialValues)}
      />
    );
  }
}

CouponForm.propTypes = {
  couponId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  addCoupon: PropTypes.func,
  updateCoupon: PropTypes.func,
  updateList: PropTypes.func,
  couponsList: PropTypes.array,
  couponData: PropTypes.object
};

const mapStateToProps = state => ({
  couponsList: CouponsSelectors.selectCouponsList(state)
});
const mapDispatchToProps = dispatch => ({
  addCoupon: coupon => dispatch(CouponsActions.addCoupon(coupon)),
  updateCoupon: coupon => dispatch(CouponsActions.updateCoupon(coupon)),
  updateList: list => dispatch(CouponsActions.updateCouponFilterList(list))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CouponForm);
